import { Loader2 } from "lucide-react";
import type { ButtonHTMLAttributes, ReactNode, Ref } from "react";
import { cx } from "../cx.ts";

export type ButtonVariant = "primary" | "accent" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

const BASE =
  "inline-flex select-none items-center justify-center gap-2 rounded-lg font-semibold " +
  "transition-colors disabled:cursor-not-allowed disabled:opacity-50 " +
  "sun:rounded-md sun:border-2 sun:border-black sun:text-min";

const VARIANTS: Record<ButtonVariant, string> = {
  // In sun the brand gives way to black and the yellow highlight: a tenant colour may not read outdoors.
  primary:
    "bg-brand text-on-brand hover:brightness-95 active:brightness-90 sun:bg-black sun:text-white",
  accent:
    "bg-brand-accent text-on-accent hover:brightness-95 active:brightness-90 sun:bg-active sun:text-on-active",
  secondary:
    "border border-line-strong bg-surface text-ink hover:bg-surface-muted active:bg-surface-muted",
  ghost: "bg-transparent text-ink hover:bg-surface-muted active:bg-surface-muted",
  danger: "bg-danger text-white hover:brightness-110 active:brightness-95",
};

// Every size keeps the theme's minimum target (44 px office, 64 px sun) as its height floor.
const SIZES: Record<ButtonSize, string> = {
  sm: "min-h-target px-3 text-sm",
  md: "min-h-target px-4 text-[0.9375rem]",
  lg: "min-h-target px-6 text-base sm:min-h-[52px]",
};

/** Button classes, for links and router <Link>s that should look like buttons. */
export function buttonClasses(
  opts: { variant?: ButtonVariant; size?: ButtonSize; block?: boolean; className?: string } = {},
): string {
  return cx(
    BASE,
    VARIANTS[opts.variant ?? "primary"],
    SIZES[opts.size ?? "md"],
    opts.block && "w-full",
    opts.className,
  );
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Full width. */
  block?: boolean;
  /** Shows a spinner, disables the button and sets aria-busy. */
  loading?: boolean;
  icon?: ReactNode;
  iconRight?: ReactNode;
  ref?: Ref<HTMLButtonElement>;
}

export function Button({
  variant,
  size,
  block,
  loading = false,
  icon,
  iconRight,
  className,
  disabled,
  children,
  type = "button",
  ref,
  ...rest
}: ButtonProps) {
  return (
    <button
      ref={ref}
      type={type}
      className={buttonClasses({
        ...(variant && { variant }),
        ...(size && { size }),
        ...(block !== undefined && { block }),
        ...(className !== undefined && { className }),
      })}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Loader2 aria-hidden className="size-4 shrink-0 animate-spin" /> : icon}
      {children}
      {!loading && iconRight}
    </button>
  );
}
